import { AxiosError } from 'axios';

import {
    BLK_API_PATHS,
    DEFAULT_INVALID_SUMMARY_RESPONSE,
    URIS,
} from '../constants';
import { ChatBlkBaseService } from '../services';
import {
    ChatBlkQuery,
    ChatBlkQueryScopeEnum,
    ChatBlkRelationshipManager,
    V1ChatExchange,
} from '../types';

/**
 * Singleton service class for handling chat-related queries.
 * @extends {ChatBlkBaseService}
 */
export class ChatBlkQueryService extends ChatBlkBaseService {
    /**
     * Singleton instance of the `ChatBlkQueryService`.
     */
    protected static instance: ChatBlkQueryService;

    /**
     * Controller to manage the lifecycle of fetch requests.
     */
    private activeQueryAbortControl: AbortController;

    /**
     * Returns the singleton instance of the `ChatBlkQueryService`.
     * If the instance does not exist and the `appContext` is available,
     * it creates a new instance.
     *
     * @returns {ChatBlkQueryService} The singleton instance.
     */

    public static getInstance(): ChatBlkQueryService {
        if (
            ChatBlkBaseService.getInstance().appContext &&
            !ChatBlkQueryService.instance
        ) {
            ChatBlkQueryService.instance = new ChatBlkQueryService();
        }

        return ChatBlkQueryService.instance;
    }

    /**
     * Fetches the summary of the content that relates in context to
     * the information on the current page.
     * @returns {Promise<Partial<ChatBlkQuery> | null>}
     */
    public fetchSummary = async (
        uri?: string
    ): Promise<Partial<ChatBlkQuery> | null> => {
        const abortController = this.abortControllerHandler();
        const { signal } = abortController;
        const headers = ChatBlkBaseService.requestHeaders;

        try {
            const request = `${ChatBlkBaseService.basePath}${BLK_API_PATHS.SUMMARIZE}`;
            const data = {
                query: {
                    uris: [this.transformSummaryUrl(uri)], // Only the user's current page for now
                },
            };
            const config = {
                headers,
                signal,
            };
            const response = await ChatBlkBaseService.axiosInstance.post(
                request,
                data,
                config
            );
            const { summaries } = response.data;
            const { summary: result = DEFAULT_INVALID_SUMMARY_RESPONSE } =
                summaries?.[0] || {};

            return {
                followUpPrompts: [],
                response: result,
                sources: [],
                scope: ChatBlkQueryScopeEnum.UNSPECIFIED,
            };
        } catch (error) {
            this.handleXhrErrors(
                'fetchQuerySummary',
                error,
                uri,
                headers,
                true
            );
            this.activeQueryAbortControl?.abort();
        } finally {
            this.cancelQuery(abortController);
        }
        return null;
    };

    /**
     * Fetches a query response from the chat exchange API.
     *
     * This method sends a POST request to the chat exchange API with the provided prompt
     * and returns a partial `ChatBlkQuery` object containing the response, related prompts,
     * sources, and scope. If an error occurs during the request, it handles the error and
     * returns `null`.
     *
     * @param prompt - The prompt string to send to the chat exchange API.
     * @returns {Promise<ChatBlkQuery | null>}
     */
    public fetchQueryResponse = async (
        prompt: string,
        promptId: string
    ): Promise<Partial<ChatBlkQuery> | null> => {
        const abortController = this.abortControllerHandler();

        const headers = ChatBlkBaseService.requestHeaders;
        const { signal } = this.activeQueryAbortControl;

        try {
            const data = {
                chatExchange: {
                    sessionId: this.sessionId,
                    chatOriginUri: window.location.href,
                    prompt,
                    promptId,
                },
            };
            const config = {
                headers,
                signal,
            };
            const request = `${ChatBlkBaseService.basePath}${BLK_API_PATHS.CHAT_EXCHANGE}`;
            const apiResponse = await ChatBlkBaseService.axiosInstance.post(
                request,
                data,
                config
            );

            const {
                relatedPrompts,
                result,
                sources,
                scope,
                id,
                sessionId,
                output,
            }: Partial<V1ChatExchange> = apiResponse.data.chatExchange;
            if (result) {
                return {
                    output,
                    followUpPrompts: relatedPrompts,
                    response: result,
                    sources,
                    scope,
                    id,
                    sessionId,
                };
            }
        } catch (error) {
            this.handleXhrErrors(
                'fetchQueryResponse',
                error,
                prompt,
                headers,
                true
            );
        }
        this.cancelQuery(abortController);
        return null;
    };

    /**
     * Fetches user's 20 most recent queries from the backend
     * as far back as the provided minDate.
     * @param {Date} minDate - The minimum date to filter queries.
     * @param {number} pageSize - The number of queries to fetch (default is 100).
     * @returns {Promise<{ prompt: string; timestamp: string }[]>} - The recent queries.
     */
    public fetchRecentQueries = async (
        minDate: Date,
        pageSize: number = 100
    ): Promise<{ prompt: string; timestamp: string }[]> => {
        const abortController = new AbortController();

        const promptRequest = {
            pageSize,
            query: {
                createTime: minDate?.toISOString(),
            },
        };
        try {
            const request = `${ChatBlkBaseService.basePath}${BLK_API_PATHS.RECENT_PROMPTS}`;
            const response = await ChatBlkBaseService.axiosInstance.post(
                request,
                promptRequest,
                {
                    headers: ChatBlkBaseService.requestHeaders,
                    signal: abortController.signal,
                }
            );
            return response.data.prompts.map(
                (q: { prompt: string; createTime: string }) => {
                    const { prompt, createTime: timestamp } = q;
                    return {
                        prompt,
                        timestamp,
                    };
                }
            );
        } catch (error: unknown) {
            this.handleXhrErrors(
                'fetchRecentQueries',
                error as AxiosError,
                promptRequest,
                ChatBlkBaseService.requestHeaders
            );
        }

        this.cancelQuery(abortController);
        return null;
    };

    /**
     * Fetches user's relationship managers
     *  @returns {Promise<ChatBlkRelationshipManager[]>} - User's relationship managers.
     */
    public fetchRelationshipManagers = async (): Promise<
        Array<ChatBlkRelationshipManager>
    > => {
        const abortController = new AbortController();

        const rmRequestData = {
            pageSize: 1,
            query: {
                userId: this.email,
            },
        };

        try {
            const request = `${ChatBlkBaseService.basePath}${BLK_API_PATHS.RELATIONSHIP_MANAGER}`;
            const response = await ChatBlkBaseService.axiosInstance.post(
                request,
                rmRequestData,
                {
                    headers: ChatBlkBaseService.requestHeaders,
                    signal: abortController.signal,
                }
            );
            return response.data
                .relationshipManagers as Array<ChatBlkRelationshipManager>;
        } catch (error: unknown) {
            this.handleXhrErrors(
                'fetchRelationshipManagers',
                error as AxiosError,
                rmRequestData,
                ChatBlkBaseService.requestHeaders
            );
        }

        this.cancelQuery(abortController);
        return null;
    };

    /**
     * Cancels the current query by aborting the associated AbortController.
     * If there is no active query, this method does nothing.
     * @returns {void}
     */
    public cancelQuery = (abortController?: AbortController): void => {
        if (abortController) {
            abortController.abort();
        } else {
            this.activeQueryAbortControl?.abort();
        }
    };

    /**
     * Creates abort controller for most recent query after aborting any
     * abort controller for any other active queries.
     * @returns {AbortController} new abort controller
     */
    private abortControllerHandler = (): AbortController => {
        this.activeQueryAbortControl?.abort();

        const abortController = new AbortController();
        this.activeQueryAbortControl = abortController;
        return abortController;
    };

    /**
     * Transforms the current URI to a standardized summary URL.
     * This function ensures that the URL is formatted correctly for summarization.
     *
     * @param {string} currentUri - The current URI to be transformed.
     * @returns {string} The transformed summary URL.
     */
    private transformSummaryUrl = (currentUri: string): string => {
        if (URIS.PRE_PROD_HOSTS.includes(window.location.hostname)) {
            return URIS.TEST_SUMMARY; // Example URL that we know works for summarization
        }

        try {
            // Use a consistent base URL
            const baseUrl = URIS.SUMMARIZE_HOSTNAME;

            // Parse the URL to extract path
            const url = new URL(currentUri);
            const pathSegments = url.pathname
                .split('/')
                .filter((segment) => segment);

            // Check if first segment is "us" - if not, we'll add it
            const firstSegment = pathSegments.length > 0 ? pathSegments[0] : '';
            const needsUs = firstSegment !== 'us';

            // Handle duplicate "financial-professionals" segments if present
            let cleanedPath = '/' + pathSegments.join('/');
            cleanedPath = cleanedPath.replace(
                '/financial-professionals/financial-professionals',
                '/financial-professionals'
            );

            // Construct the final URL with baseUrl + "/us" + cleaned path
            return baseUrl + (needsUs ? '/us' : '') + cleanedPath;
        } catch (error) {
            console.error('Error transforming URL:', error);
            return currentUri; // Return original if parsing fails
        }
    };
}
